import {useEffect, useMemo, useState} from "react";
import {useFormikContext} from "formik";
import {CountrySelector} from "react-international-phone";
import "react-international-phone/style.css";
import {connect} from "react-redux";
import {useTranslation} from "react-i18next";

import {verifyCodeOTP, verifyTelephone} from "../../../services/AuthService";
import {
  getOtpErrorMessage,
  isValidOtpPhone,
  normalizePhoneForOtp,
} from "../../../utils/otp";
import SixDigitInput from "./CodeInputTel";
import {showToastModal} from "./ToastModalSwal";
import TurnstileWidget from "./TurnstileWidget";

function TelVerification({
  setPhoneNumber,
  phone,
  country,
  setCountry,
  dialCode,
  setDialCode,
  setStep,
  turnstileToken,
  turnstileRequired,
  onTurnstileConsumed,
  ...props
}) {
  const {i18n} = useTranslation();
  const {handleSubmit} = useFormikContext();
  const [errors, setErrors] = useState({phone: "", code: ""});
  const [code, setCode] = useState(new Array(6).fill(""));
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendDelay, setResendDelay] = useState(0);
  const [localTurnstileToken, setLocalTurnstileToken] = useState(null);
  const [localTurnstileRequired, setLocalTurnstileRequired] = useState(true);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const normalizedPhone = useMemo(
    () => normalizePhoneForOtp(dialCode, phone),
    [dialCode, phone],
  );

  useEffect(() => {
    if (resendDelay <= 0) return undefined;
    const timer = window.setInterval(
      () => setResendDelay((current) => Math.max(0, current - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [resendDelay]);

  const maskedPhone = normalizedPhone
    ? `${"•".repeat(Math.max(0, normalizedPhone.length - 4))}${normalizedPhone.slice(-4)}`
    : "";

  const requestCode = async () => {
    if (!isValidOtpPhone(normalizedPhone)) {
      setErrors((current) => ({
        ...current,
        phone: "Le numéro de téléphone n'est pas valide.",
      }));
      return;
    }
    if (sending || resendDelay > 0) return;

    const captchaToken = turnstileToken || localTurnstileToken;
    const captchaRequired = turnstileToken
      ? turnstileRequired
      : localTurnstileRequired;
    if (captchaRequired && !captchaToken) {
      setErrors((current) => ({
        ...current,
        phone: "Veuillez valider la protection anti-robot.",
      }));
      return;
    }

    setSending(true);
    setErrors({phone: "", code: ""});
    try {
      await verifyTelephone({
        email: props.email,
        phone,
        dialCode,
        language: i18n.language.slice(0, 2),
        turnstileToken: captchaToken || "",
      });
      setCode(new Array(6).fill(""));
      setCodeSent(true);
      setResendDelay(60);
    } catch (error) {
      const message = getOtpErrorMessage(error, "send");
      setErrors((current) => ({...current, phone: message}));
      if (/déjà associée/i.test(message)) {
        showToastModal({
          success: false,
          title: "Adresse e-mail déjà utilisée",
          message,
          thenCallback: () => setStep(false),
          duration: 5000,
        });
      }
    } finally {
      if (turnstileToken) onTurnstileConsumed?.();
      setLocalTurnstileToken(null);
      setTurnstileResetKey((current) => current + 1);
      setSending(false);
    }
  };

  const confirmCode = async () => {
    const value = code.join("");
    if (!/^\d{6}$/.test(value)) {
      setErrors((current) => ({
        ...current,
        code: "Le code de vérification n'est pas valide.",
      }));
      return;
    }

    setVerifying(true);
    setErrors((current) => ({...current, code: ""}));
    try {
      await verifyCodeOTP({phone, dialCode, code: value});
      await handleSubmit();
    } catch (error) {
      setErrors((current) => ({
        ...current,
        code: getOtpErrorMessage(error, "verify"),
      }));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div className="w-full 2xl:w-3/4">
        <div className="mb-5">
          <h2 className="text-left text-[28px] font-bold uppercase text-primary">
            Vérification du numéro de téléphone
          </h2>
          <p className="mt-2 text-sm leading-6 text-[#667085]">
            Cette étape sécurise votre compte. Nous vous enverrons un code unique
            par SMS ; aucun message commercial ne sera envoyé.
          </p>
        </div>

        {!codeSent ? (
          <div className="flex w-full flex-col gap-4">
            <div className="form-group">
              <label className="mb-2 font-medium">Numéro de téléphone</label>
              <div className="flex items-center justify-start gap-2">
                <CountrySelector
                  className="flex items-center justify-start"
                  dropdownArrowClassName="absolute right-0"
                  flagClassName="absolute left-2"
                  selectedCountry={country}
                  onSelect={(selected) => {
                    setCountry(selected.iso2);
                    setDialCode(`+${selected.dialCode}`);
                    setErrors((current) => ({...current, phone: ""}));
                  }}
                  renderButtonWrapper={({children, rootProps}) => (
                    <button
                      type="button"
                      {...rootProps}
                      className="relative h-[51px] w-[100px] rounded-lg border bg-white max-[1400px]:h-[41px]"
                    >
                      {children}
                      <span className="absolute right-5 top-[12px]">{dialCode}</span>
                    </button>
                  )}
                />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  className="form-control"
                  value={phone}
                  placeholder="6 12 34 56 78"
                  onChange={(event) => {
                    setPhoneNumber(event.target.value);
                    setErrors((current) => ({...current, phone: ""}));
                  }}
                />
              </div>
              {errors.phone && <div className="mt-1 text-danger fs-12">{errors.phone}</div>}
            </div>
            {!turnstileToken && (
              <TurnstileWidget
                onTokenChange={setLocalTurnstileToken}
                onRequirementChange={setLocalTurnstileRequired}
                resetKey={turnstileResetKey}
              />
            )}
            <button
              type="button"
              disabled={sending}
              onClick={requestCode}
              className="btn btn-block uppercase disabled:opacity-60"
              style={{
                backgroundColor: "#1a71b9",
                borderColor: "#1a71b9",
                color: "#fff",
              }}
            >
              {sending ? "Envoi en cours…" : "Envoyer le code de vérification"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <p className="text-base font-medium text-[#3D4465]">
              Un code à six chiffres a été envoyé au {maskedPhone}.
            </p>
            <div className="form-group">
              <label className="mb-2 font-medium">Code de vérification</label>
              <SixDigitInput values={code} setValues={setCode} />
              {errors.code && <div className="mt-1 text-danger fs-12">{errors.code}</div>}
            </div>
            <TurnstileWidget
              onTokenChange={setLocalTurnstileToken}
              onRequirementChange={setLocalTurnstileRequired}
              resetKey={turnstileResetKey}
            />
            <div className="flex items-center justify-between text-sm font-medium">
              <button
                type="button"
                className="text-[#1A64B9] underline"
                onClick={() => {
                  setCodeSent(false);
                  setCode(new Array(6).fill(""));
                }}
              >
                Modifier le numéro
              </button>
              <button
                type="button"
                disabled={resendDelay > 0 || sending}
                className="text-[#1A64B9] underline disabled:cursor-not-allowed disabled:text-[#98A2B3]"
                onClick={requestCode}
              >
                {resendDelay > 0 ? `Renvoyer dans ${resendDelay} s` : "Renvoyer le code"}
              </button>
            </div>
            <button
              type="button"
              disabled={verifying}
              onClick={confirmCode}
              className="btn btn-block uppercase disabled:opacity-60"
              style={{
                backgroundColor: "#1a71b9",
                borderColor: "#1a71b9",
                color: "#fff",
              }}
            >
              {verifying ? "Vérification…" : "Valider mon numéro"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  errorMessage: state.auth.errorMessage,
  successMessage: state.auth.successMessage,
  showLoading: state.auth.showLoading,
});

export default connect(mapStateToProps)(TelVerification);
